import { useQuery } from '@tanstack/react-query';
import { productsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

export const useProducts = (params = {}, options = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['products', queryParams], // Include lang in cache key
    queryFn: () => productsAPI.getProducts(queryParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Optional gate (e.g. wait for a vendor UUID to resolve before fetching).
    // Defaults to true to preserve existing single-arg call sites.
    enabled: options.enabled !== undefined ? !!options.enabled : true
  });
};

export const useProduct = (id, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['product', id, queryParams], // Include params with lang
    queryFn: () => productsAPI.getProductById(id, queryParams),
    enabled: !!id,
    staleTime: 1000 * 60 * 10 // 10 minutes
  });
};

// Quick view hook - fetches minimal product data for preview modals
export const useProductQuickView = (id, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['productQuickView', id, queryParams],
    queryFn: () => productsAPI.getProductQuickView(id, queryParams),
    enabled: !!id,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
};

// Related products hook - uses 3 strategies for recommendations
export const useRelatedProducts = (id, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['relatedProducts', id, queryParams],
    queryFn: () => productsAPI.getRelatedProducts(id, queryParams),
    enabled: !!id,
    staleTime: 1000 * 60 * 15 // 15 minutes - recommendations change less frequently
  });
};

// Products by category slug hook
export const useProductsByCategory = (categorySlug, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['productsByCategory', categorySlug, queryParams],
    queryFn: () => productsAPI.getProductsByCategory(categorySlug, queryParams),
    enabled: !!categorySlug,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
};

// Product filter with facets hook
export const useProductsFilter = (params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['productsFilter', queryParams],
    queryFn: () => productsAPI.getProductsFilter(queryParams),
    staleTime: 1000 * 60 * 3 // 3 minutes - filter data changes more frequently
  });
};

// Search products hook with ranking
export const useSearchProducts = (searchTerm, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['products', 'search', searchTerm, queryParams], // Include lang in cache key
    queryFn: () => productsAPI.searchProducts(searchTerm, queryParams),
    enabled: !!searchTerm,
    staleTime: 1000 * 60 * 2 // 2 minutes
  });
};

// Compare products hook
export const useCompare = (productIds, params = {}) => {
  const { language } = useLanguage();

  // Merge language into params
  const queryParams = { ...params, lang: language };

  return useQuery({
    queryKey: ['products', 'compare', productIds, queryParams],
    queryFn: () => productsAPI.compareProducts(productIds, queryParams),
    enabled: productIds && productIds.length >= 2,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
};

// Price history hook - daily price trend + lowest-in-range summary.
// The endpoint doesn't use lang, so no language is merged in.
// 404 (genuinely unknown product) is not retried — on a loaded product page it
// indicates the product has no detail row, not a transient failure.
export const usePriceHistory = (id, range = '30d') => {
  return useQuery({
    queryKey: ['priceHistory', id, range],
    queryFn: () => productsAPI.getPriceHistory(id, { range }),
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes — matches the BE LRU/Cache-Control TTL
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    }
  });
};
